// OperationsHub — central operations dashboard with KPIs, quick links, and activity feed
import { useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Cog,
  ListTodo,
  Users,
  Target,
  AlertOctagon,
  ShieldCheck,
  Trash2,
  Plus,
  Clock,
  ArrowRight,
  Activity,
  CheckCircle,
  AlertTriangle,
  Megaphone,
  CalendarClock,
  BarChart3,
} from "lucide-react";
import { Button } from "@cieda/djayma-ui";
import { toast } from "sonner";
import AdminLayout from "@/components/admin/AdminLayout";
import { useAdminPageTitle } from "@/hooks/admin/useAdminPageTitle";
import { KPICard } from "@/components/admin/shared/KPICard";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import {
  useTasks,
  useCreateTask,
  useIncidents,
  useSLA,
  useAdminUsers,
} from "@/hooks/admin/useOperations";
import type { CreateTaskInput, TaskPriority } from "@/lib/admin-api/operations";

// ---------------------------------------------------------------------------
// Navigation cards config
// ---------------------------------------------------------------------------

interface NavCard {
  readonly label: string;
  readonly description: string;
  readonly href: string;
  readonly icon: React.ReactNode;
  readonly color: string;
}

const NAV_CARDS: readonly NavCard[] = [
  {
    label: "Taches",
    description: "Gestion des taches operationnelles",
    href: "/admin/tasks",
    icon: <ListTodo className="h-5 w-5" />,
    color: "bg-blue-50 text-blue-700 border-blue-200",
  },
  {
    label: "Planning equipes",
    description: "Horaires et shifts des agents",
    href: "/admin/shifts",
    icon: <CalendarClock className="h-5 w-5" />,
    color: "bg-emerald-50 text-emerald-700 border-emerald-200",
  },
  {
    label: "SLA",
    description: "Suivi du niveau de service",
    href: "/admin/sla",
    icon: <Target className="h-5 w-5" />,
    color: "bg-amber-50 text-amber-700 border-amber-200",
  },
  {
    label: "Incidents",
    description: "Suivi et resolution des incidents",
    href: "/admin/incidents",
    icon: <AlertOctagon className="h-5 w-5" />,
    color: "bg-red-50 text-red-700 border-red-200",
  },
  {
    label: "Utilisateurs admin",
    description: "Gestion des comptes administrateurs",
    href: "/admin/admin-users",
    icon: <Users className="h-5 w-5" />,
    color: "bg-violet-50 text-violet-700 border-violet-200",
  },
  {
    label: "Roles et permissions",
    description: "Configuration RBAC",
    href: "/admin/roles",
    icon: <ShieldCheck className="h-5 w-5" />,
    color: "bg-stone-50 text-stone-700 border-stone-200",
  },
  {
    label: "Performance agents",
    description: "Metriques et classement des agents",
    href: "/admin/agent-performance",
    icon: <BarChart3 className="h-5 w-5" />,
    color: "bg-cyan-50 text-cyan-700 border-cyan-200",
  },
  {
    label: "Annonces",
    description: "Communications internes",
    href: "/admin/announcements",
    icon: <Megaphone className="h-5 w-5" />,
    color: "bg-orange-50 text-orange-700 border-orange-200",
  },
  {
    label: "Suppressions de comptes",
    description: "Demandes de suppression RGPD",
    href: "/admin/operations/account-deletions",
    icon: <Trash2 className="h-5 w-5" />,
    color: "bg-rose-50 text-rose-700 border-rose-200",
  },
];

// ---------------------------------------------------------------------------
// Quick task creation modal
// ---------------------------------------------------------------------------

interface QuickTaskModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onSave: (input: CreateTaskInput) => Promise<void>;
  readonly isSaving: boolean;
}

function QuickTaskModal({ open, onClose, onSave, isSaving }: QuickTaskModalProps) {
  const [title, setTitle] = useState("");
  const [priority, setPriority] = useState<TaskPriority>("medium");
  const [dueDate, setDueDate] = useState("");
  const [error, setError] = useState<string | null>(null);

  const handleClose = useCallback(() => {
    setTitle("");
    setPriority("medium");
    setDueDate("");
    setError(null);
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(async () => {
    if (!title.trim()) {
      setError("Le titre est requis.");
      return;
    }
    setError(null);
    try {
      await onSave({
        title: title.trim(),
        priority,
        due_date: dueDate || undefined,
      });
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Une erreur est survenue.");
    }
  }, [title, priority, dueDate, onSave, handleClose]);

  return (
    <AdminModal
      open={open}
      onClose={handleClose}
      title="Nouvelle tache rapide"
      size="sm"
      footer={
        <>
          <Button variant="outline" onClick={handleClose} disabled={isSaving}>
            Annuler
          </Button>
          <Button onClick={handleSubmit} disabled={isSaving} className="gap-1.5">
            {isSaving && (
              <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
            )}
            Creer
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <label className="mb-1.5 block text-sm font-medium text-stone-700">
            Titre <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Ex: Verifier stock entrepot Dakar"
            className="h-10 w-full rounded-md border border-stone-200 px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
          />
        </div>
        <div>
          <label className="mb-1.5 block text-sm font-medium text-stone-700">
            Priorite
          </label>
          <select
            value={priority}
            onChange={(e) => setPriority(e.target.value as TaskPriority)}
            className="h-10 w-full rounded-md border border-stone-200 bg-white px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
          >
            <option value="low">Basse</option>
            <option value="medium">Moyenne</option>
            <option value="high">Haute</option>
            <option value="urgent">Urgente</option>
          </select>
        </div>
        <div>
          <label className="mb-1.5 block text-sm font-medium text-stone-700">
            Date limite
          </label>
          <input
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            className="h-10 w-full rounded-md border border-stone-200 px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
          />
        </div>
        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}
      </div>
    </AdminModal>
  );
}

// ---------------------------------------------------------------------------
// Recent activity feed (derived from tasks + incidents)
// ---------------------------------------------------------------------------

interface ActivityItem {
  readonly id: string;
  readonly type: "task_created" | "task_done" | "incident_open" | "incident_resolved";
  readonly label: string;
  readonly detail: string;
  readonly timestamp: string;
}

function ActivityFeed({ items }: { readonly items: readonly ActivityItem[] }) {
  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-10 text-center">
        <Activity className="h-8 w-8 text-stone-300" />
        <p className="mt-2 text-sm text-stone-500">Aucune activite recente</p>
      </div>
    );
  }

  const iconMap: Record<ActivityItem["type"], React.ReactNode> = {
    task_created: <Plus className="h-3.5 w-3.5 text-blue-600" />,
    task_done: <CheckCircle className="h-3.5 w-3.5 text-emerald-600" />,
    incident_open: <AlertTriangle className="h-3.5 w-3.5 text-red-600" />,
    incident_resolved: <CheckCircle className="h-3.5 w-3.5 text-emerald-600" />,
  };

  const bgMap: Record<ActivityItem["type"], string> = {
    task_created: "bg-blue-50",
    task_done: "bg-emerald-50",
    incident_open: "bg-red-50",
    incident_resolved: "bg-emerald-50",
  };

  return (
    <div className="space-y-3">
      {items.map((item) => (
        <div key={item.id} className="flex items-start gap-3">
          <div className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${bgMap[item.type]}`}>
            {iconMap[item.type]}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-stone-900">{item.label}</p>
            <p className="truncate text-xs text-stone-500">{item.detail}</p>
          </div>
          <span className="shrink-0 text-xs text-stone-400">
            {new Date(item.timestamp).toLocaleDateString("fr-SN")}
          </span>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export default function OperationsHub() {
  useAdminPageTitle("Operations");
  const [quickTaskOpen, setQuickTaskOpen] = useState(false);

  // Fetch data for KPIs
  const { data: tasksData, isLoading: tasksLoading } = useTasks({ limit: 50 });
  const { data: incidentsData, isLoading: incidentsLoading } = useIncidents({ limit: 50 });
  const { data: slaData, isLoading: slaLoading } = useSLA();
  const { data: usersData, isLoading: usersLoading } = useAdminUsers({ limit: 50, is_active: true });

  const createTask = useCreateTask();

  // Derived KPIs
  const tasks = tasksData?.tasks ?? [];
  const incidents = incidentsData?.incidents ?? [];
  const slaStatuses = slaData?.statuses ?? [];
  const adminUsers = usersData?.users ?? [];

  const openTasks = useMemo(
    () => tasks.filter((t) => t.status === "todo" || t.status === "in_progress").length,
    [tasks],
  );

  const slaCompliance = useMemo(() => {
    if (slaStatuses.length === 0) return 0;
    const avg = slaStatuses.reduce((sum, s) => sum + s.compliance_percent, 0) / slaStatuses.length;
    return Math.round(avg * 10) / 10;
  }, [slaStatuses]);

  const activeIncidents = useMemo(
    () => incidents.filter((i) => i.status === "open" || i.status === "investigating").length,
    [incidents],
  );

  const onlineTeam = useMemo(() => adminUsers.length, [adminUsers]);

  // Build activity feed from recent tasks and incidents
  const activityItems: readonly ActivityItem[] = useMemo(() => {
    const items: ActivityItem[] = [];

    for (const t of tasks.slice(0, 10)) {
      if (t.status === "done" && t.completed_at) {
        items.push({
          id: `task-done-${t.id}`,
          type: "task_done",
          label: "Tache terminee",
          detail: t.title,
          timestamp: t.completed_at,
        });
      } else {
        items.push({
          id: `task-${t.id}`,
          type: "task_created",
          label: "Tache creee",
          detail: t.title,
          timestamp: t.created_at,
        });
      }
    }

    for (const i of incidents.slice(0, 10)) {
      if (i.resolved_at) {
        items.push({
          id: `incident-resolved-${i.id}`,
          type: "incident_resolved",
          label: "Incident resolu",
          detail: i.title,
          timestamp: i.resolved_at,
        });
      } else {
        items.push({
          id: `incident-${i.id}`,
          type: "incident_open",
          label: `Incident ${i.severity === "critical" ? "critique" : "ouvert"}`,
          detail: i.title,
          timestamp: i.created_at,
        });
      }
    }

    return [...items].sort(
      (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
    ).slice(0, 12);
  }, [tasks, incidents]);

  const handleQuickTaskSave = useCallback(
    async (input: CreateTaskInput) => {
      await createTask.mutateAsync(input);
      toast.success("Tache creee avec succes");
    },
    [createTask],
  );

  const isLoading = tasksLoading || incidentsLoading || slaLoading || usersLoading;

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
              <Cog className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-display text-xl font-semibold text-stone-900">
                Operations
              </h1>
              <p className="text-sm text-stone-500">
                Gestion des taches, equipes et suivi operationnel
              </p>
            </div>
          </div>
          <Button onClick={() => setQuickTaskOpen(true)} className="gap-1.5">
            <Plus className="h-4 w-4" />
            Nouvelle tache
          </Button>
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <KPICard
            label="Taches ouvertes"
            value={isLoading ? 0 : openTasks}
            format="number"
            icon={<ListTodo className="h-4 w-4" />}
          />
          <KPICard
            label="SLA respecte"
            value={isLoading ? 0 : slaCompliance}
            format="percent"
            icon={<Target className="h-4 w-4" />}
          />
          <KPICard
            label="Incidents actifs"
            value={isLoading ? 0 : activeIncidents}
            format="number"
            icon={<AlertOctagon className="h-4 w-4" />}
          />
          <KPICard
            label="Equipe en ligne"
            value={isLoading ? 0 : onlineTeam}
            format="number"
            icon={<Users className="h-4 w-4" />}
          />
        </div>

        {/* Main grid: nav cards + activity feed */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* Navigation cards — 2 columns on the left */}
          <div className="lg:col-span-2">
            <h2 className="mb-4 text-sm font-semibold text-stone-700">
              Modules operationnels
            </h2>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {NAV_CARDS.map((card) => (
                <Link
                  key={card.href}
                  to={card.href}
                  className="group flex items-start gap-3 rounded-lg border border-stone-200 bg-white p-4 transition-all hover:border-stone-300 hover:shadow-md"
                >
                  <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border ${card.color}`}>
                    {card.icon}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-stone-900 group-hover:text-stone-700">
                      {card.label}
                    </p>
                    <p className="mt-0.5 text-xs text-stone-500">
                      {card.description}
                    </p>
                  </div>
                  <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-stone-300 transition-transform group-hover:translate-x-0.5 group-hover:text-stone-500" />
                </Link>
              ))}
            </div>
          </div>

          {/* Activity feed — right column */}
          <div>
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-stone-700">
                Activite recente
              </h2>
              <div className="flex items-center gap-1 text-xs text-stone-400">
                <Clock className="h-3.5 w-3.5" />
                Mise a jour en continu
              </div>
            </div>
            <div className="rounded-lg border border-stone-200 bg-white p-4">
              {isLoading ? (
                <div className="space-y-3">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="h-7 w-7 animate-pulse rounded-full bg-stone-100" />
                      <div className="flex-1 space-y-1.5">
                        <div className="h-3.5 w-2/3 animate-pulse rounded bg-stone-100" />
                        <div className="h-3 w-1/2 animate-pulse rounded bg-stone-100" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <ActivityFeed items={activityItems} />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Quick task modal */}
      <QuickTaskModal
        open={quickTaskOpen}
        onClose={() => setQuickTaskOpen(false)}
        onSave={handleQuickTaskSave}
        isSaving={createTask.isPending}
      />
    </AdminLayout>
  );
}
